import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  Paper,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory2 as Inventory2Icon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  TwoWheeler as TwoWheelerIcon,
  Storefront as StorefrontIcon,
} from "@mui/icons-material";
import {
  useProductosMaterialContext,
  guardarFacturaBackend,
  enviarPayloadFacturaElectronica,
  emitirEcfDgii,
  revertirFacturaRechazada,
  empresaProductosDesdeContexto,
} from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import PagoFactura from "facturapos/PagoFactura";
import VistaFactura from "facturapos/VistaFactura";
import tiposDocumentosJson from "utils/tiposDocumentos.json";
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";
import { CONFIGURACION_IMPRESION_CHASIS, tituloComprobante } from "lib/impresionChasis";
import { hoyLocal } from "lib/fechaLocal";

const TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO = "Ventas Contado";

/**
 * Placa: monto fijo por chasis, venta exenta (tasa 0 → e-CF indicador 4).
 * Una sola línea por factura con el total exento (cantidad = número de
 * chasis × monto fijo). Va con tipo "cargo": se factura pero no mueve
 * inventario.
 */
export const CODIGO_PLACA = "PLACA";
const CLAVE_MONTO_PLACA = "fe-dgii:montoPlaca";

export const lineaPlacaTotal = (cantidadChasis, montoFijo) => ({
  id: "placa",
  key: "placa",
  esPlaca: true,
  chasis: null,
  noprest: null,
  descripcion: "PLACA (EXENTA)",
  cantidad: cantidadChasis,
  precio: montoFijo,
  descuento: 0,
  tasaitbis: "0",
});

const leerMontoPlaca = (usuario) => {
  try {
    return window.localStorage.getItem(`${CLAVE_MONTO_PLACA}:${usuario || ""}`) || "";
  } catch (e) {
    return "";
  }
};

const recordarMontoPlaca = (usuario, monto) => {
  try {
    window.localStorage.setItem(`${CLAVE_MONTO_PLACA}:${usuario || ""}`, String(monto));
  } catch (e) {
    // Sin almacenamiento: la próxima placa se escribe a mano.
  }
};

/**
 * Datos para VistaFactura desde la factura de chasis ya guardada. Los ítems
 * llevan la tasa en `impuesto`, como el POS, y el subtotal de la línea ya neto
 * del descuento general prorrateado.
 *
 * Siempre en hoja grande: `configuracion` va fija y VistaFactura no consulta
 * el formato elegido en el POS (que suele ser el ticket de 80 mm).
 */

export const datosVistaFactura = ({
  payload,
  totales,
  numero,
  documento,
  tipoDocumento,
  idEmpresa,
  emisor,
  fechaFirma,
  codigoSeguridad,
  montoTotalEcf,
  ambiente,
  usuario,
}) => {
  const items = payload.items || [];
  const importeTotal = items.reduce((s, it) => s + it.cantidad * it.valor, 0);
  const descGeneral = Number(payload.descuentogeneral) || 0;
  return {
    items: items.map((it) => {
      const importe = it.cantidad * it.valor;
      const descGen = importeTotal ? descGeneral * (importe / importeTotal) : 0;
      return {
        codigo: it.chasis || "",
        descripcion: it.item,
        subdescripcion: it.subdescripcion || "",
        cantidad: it.cantidad,
        precio: it.valor,
        descuento: it.descuento,
        descuento_general: descGen,
        subtotal: Math.max(0, importe - it.descuento - descGen),
        impuesto: it.tasaitbis,
        tasaitbis: it.tasaitbis,
      };
    }),
    impuestos: totales.itbis > 0 ? [{ nombre: "ITBIS", monto: totales.itbis }] : [],
    subtotal: totales.subtotal - totales.descTotal,
    totalDescuentos: totales.descTotal,
    totalImpuestos: totales.itbis,
    montoExento: totales.exento,
    total: totales.totalGeneral,
    documento: documento || "",
    numero: numero != null ? String(numero) : "",
    fecha: hoyLocal(),
    ncf: payload.comprobante,
    tipoDocumento: tipoDocumento || "FACTURA",
    tituloDocumento:
      tituloComprobante(payload.comprobante, payload.codigocomprobante) ||
      String(tipoDocumento || "FACTURA").split(":")[0].trim(),
    observaciones: payload.garantia || "",
    id_empresa: idEmpresa,
    comprobanteelectronico: payload.comprobanteelectronico,
    codigocomprobante: payload.codigocomprobante,
    fechavencimiento: payload.fechavencimiento,
    rnc: payload.rnc,
    cliente_nombre: payload.razonsocial,
    fecha_firma: fechaFirma || "",
    codigo_seguridad: codigoSeguridad || "",
    monto_total_ecf: montoTotalEcf,
    ambiente: ambiente || "",
    emisor,
    usuario,
    pago: payload.pago,
    configuracion: CONFIGURACION_IMPRESION_CHASIS,
  };
};

/** Sub-descripción de la línea: el dueño del chasis, si se indicó. */
export const subdescripcionItem = (it) => {
  const dueno = String(it?.dueno || "").trim();
  return dueno ? `DUEÑO: ${dueno}` : "";
};

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === "false") return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || "").trim().toLowerCase() === "contabilidad");
};

const enviarMotorContableActivo = (valor) => String(valor || "").trim().toUpperCase() === "S";

const debeEnviarAlMotor = (modulos, enviarMotorContable) =>
  moduloContabilidadInstalado(modulos) && enviarMotorContableActivo(enviarMotorContable);

const bloqueMotorDeTipo = (tipooperacion) => {
  const clave = String(tipooperacion || "").toLowerCase().replace(/\s/g, "");
  if (!clave) return null;
  return tiposDocumentosJson.find((e) => e?.[clave])?.[clave] || null;
};

const origenFormaPagoReglas = (tipooperacion) => {
  const bloque = bloqueMotorDeTipo(tipooperacion);
  if (!bloque) return [];
  const raw = bloque.origenformapago ?? bloque.origenformadepago ?? [];
  return (Array.isArray(raw) ? raw : [raw]).map((x) => String(x || "").trim()).filter(Boolean);
};

const tipoTieneOrigenFormaPago = (tipooperacion) => origenFormaPagoReglas(tipooperacion).length > 0;

const payloadFormaPagoSiAplica = (tipoDocumento, documentoFormaPago) => {
  const tipo = String(tipoDocumento || "").trim();
  const documento = String(documentoFormaPago || "").trim();
  if (!tipo || !tipoTieneOrigenFormaPago(tipo) || !documento) return {};
  return { forma_pago: documento };
};

const codigoUsaOrigenFormaPago = (tipooperacion, codigoRegla) => {
  const codigo = String(codigoRegla || "").trim().toLowerCase();
  if (!codigo) return false;
  return origenFormaPagoReglas(tipooperacion).some((c) => c.toLowerCase() === codigo);
};

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || "").trim();
  if (!tipo) return false;
  return (registros || []).some((r) => {
    if (String(r?.tipo_documento || "").trim() !== tipo) return false;
    if (codigoUsaOrigenFormaPago(tipo, r?.codigo_regla)) return true;
    const aplica = String(r?.aplica_a || "cuenta").trim().toLowerCase();
    if (aplica === "json") return true;
    return Boolean(String(r?.cuentacontable || "").trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || "").trim();
  return (registros || []).filter((r) => String(r?.tipo_documento || "").trim() === tipo);
};

const CLAVES_META_MOTOR = new Set([
  "tipodocumento",
  "nfactura",
  "documento",
  "numero",
  "suplidor",
  "fecha",
  "tasa",
  "usuario",
  "concepto",
  "valor",
  "valor_total",
  "lineas",
  "cuentas",
]);

const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const construirDocumentoMotorVentasContado = (reglas, ctx) => {
  const tipoDoc = TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO;
  const lineasFp = Array.isArray(ctx.lineas) ? ctx.lineas.filter((ln) => ln?.cuenta && (ln.monto || 0) > 0) : [];
  const valor = redondearDinero2(ctx.valorTotal);
  const montos = ctx.montos || {};
  const doc = {
    tipodocumento: tipoDoc,
    nfactura: ctx.nfactura,
    documento: ctx.documento,
    numero: ctx.numero,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor,
    lineas: lineasFp,
    permitir_descuadre: true,
  };
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || "").trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    const usaFp =
      codigoUsaOrigenFormaPago(tipoDoc, codigo) ||
      String(regla?.aplica_a || "").trim().toLowerCase() === "json";
    if (usaFp) {
      doc[codigo] = lineasFp;
      return;
    }
    const extra = montos[codigo] ?? montos[codigo.toLowerCase()];
    doc[codigo] = redondearDinero2(extra != null ? extra : valor);
  });
  return doc;
};

const pagosMetodoParaMotor = (datosPago) =>
  Object.entries(datosPago?.pagos || {}).filter(
    ([clave, pago]) => clave !== "nota_credito" && (parseFloat(pago?.monto) || 0) > 0
  );

const resolverFilaFormaPago = (pago, formasPagoDb) => {
  const doc = String(pago?.documento || pago?.metodo || "").trim();
  if (!doc) return null;
  const norm = doc.toLowerCase();
  return (
    (formasPagoDb || []).find((r) => String(r.documento || "").trim().toLowerCase() === norm) ||
    (formasPagoDb || []).find((r) => String(r.documento || "").trim().toLowerCase().includes(norm)) ||
    (formasPagoDb || []).find((r) => norm.includes(String(r.documento || "").trim().toLowerCase())) ||
    null
  );
};

const documentoFormaPagoDesdePago = (datosPago, formasPagoDb) => {
  const pagos = pagosMetodoParaMotor(datosPago);
  const primero = pagos[0]?.[1];
  const fila = resolverFilaFormaPago(primero, formasPagoDb);
  return String(primero?.documento || fila?.documento || datosPago?.metodoPrincipal || "").trim();
};

const lineasMotorDesdePago = (datosPago, formasPagoDb) =>
  pagosMetodoParaMotor(datosPago)
    .map(([, pago]) => {
      const fila = resolverFilaFormaPago(pago, formasPagoDb);
      const cuenta = String(pago?.cuenta_contable || fila?.cuenta_contable || "").trim();
      const monto = redondearDinero2(pago?.monto);
      return cuenta && monto > 0 ? { cuenta, monto } : null;
    })
    .filter(Boolean);

const TASAS_ITBIS = [
  { value: "18", label: "18%" },
  { value: "16", label: "16%" },
  { value: "0", label: "Exento" },
];

/** Códigos e-CF permitidos (sufijo del nombre, p.ej. "CREDITO FISCAL:E31" → 31). */
const CODIGOS_DOCUMENTO_DGII = ["31", "32", "44", "45"];

/** Extrae el código numérico del tipo fiscal (campo codigo o sufijo tras ":" en nombre). */
const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || "").replace(/\D/g, "");
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || "").split(":")[1]?.replace(/\D/g, "") || "";
};

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: "hidden",
};

const itemHeadLabelSx = {
  fontWeight: 700,
  fontSize: "0.72rem",
  color: FP.color.textSecondary,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

const itemsTableSx = {
  tableLayout: "fixed",
  width: "100%",
  minWidth: { xs: 760, sm: 860 },
};

const itemsAnchosColumnas = ["13%", "27%", "13%", "13%", "10%", "12%", "12%", "6%"];
const itemsColgroup = (
  <colgroup>
    {itemsAnchosColumnas.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);

function toNumber(value) {
  return parseFloat(String(value ?? "").replace(/,/g, "")) || 0;
}

function formatMoney(value) {
  return (Number(value) || 0).toLocaleString("es-DO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const FacturasElectronicasDgii = () => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    comprobanteelectronico,
    nombreNegocio,
    rnc_empresa,
    direccion_empresa,
    modulos,
    enviarMotorContable,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || "";

  // Datos fiscales
  const hoy = hoyLocal();
  const [tiposComprobante, setTiposComprobante] = useState([]);
  const [tipoComprobante, setTipoComprobante] = useState("");
  const [comprobante, setComprobante] = useState("");
  const [rnc, setRnc] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [opcionesRnc, setOpcionesRnc] = useState([]);
  const [cargandoRnc, setCargandoRnc] = useState(false);
  const busquedaRncTimerRef = useRef(null);
  const [garantia, setGarantia] = useState("");

  // Detalle de la factura
  const [itemsFactura, setItemsFactura] = useState([]);
  // Placa exenta: una línea total (chasis × monto fijo), no una por chasis.
  const [incluirPlaca, setIncluirPlaca] = useState(false);
  const [montoPlaca, setMontoPlaca] = useState("");
  const lineasFactura = useMemo(
    () =>
      incluirPlaca && itemsFactura.length > 0
        ? [...itemsFactura, lineaPlacaTotal(itemsFactura.length, montoPlaca)]
        : itemsFactura,
    [itemsFactura, incluirPlaca, montoPlaca]
  );

  // Cargos globales
  const [envio, setEnvio] = useState("0.00");
  const [otrosCargos, setOtrosCargos] = useState("0.00");
  const [descuentoGeneral, setDescuentoGeneral] = useState("0.00");

  // Autocomplete de chasis (tabla prestamo, saldados)
  const [opcionesChasis, setOpcionesChasis] = useState([]);
  const [inputChasis, setInputChasis] = useState("");
  const [cargandoChasis, setCargandoChasis] = useState(false);

  // Facturas x mayor (tabla factcxcmayor) y sus chasis
  const [modoOrigen, setModoOrigen] = useState("saldados");
  const [facturasMayor, setFacturasMayor] = useState([]);
  const [inputMayor, setInputMayor] = useState("");
  const [cargandoMayor, setCargandoMayor] = useState(false);

  const [generando, setGenerando] = useState(false);
  const [dialogoPagoAbierto, setDialogoPagoAbierto] = useState(false);
  const [formasPagoDb, setFormasPagoDb] = useState([]);

  // Datos de la factura ya generada, para armar la impresion fiscal (mismo JSON
  // enviado al backend para el Excel del e-CF).
  const [dialogoImpresionAbierto, setDialogoImpresionAbierto] = useState(false);
  const [datosImpresion, setDatosImpresion] = useState(null);

  // Documento fiscal seleccionado (tabla tipos_documentos_fiscales, como FacturaPOS).
  const documentoSeleccionado = useMemo(
    () => tiposComprobante.find((t) => t.nombre === tipoComprobante) || null,
    [tiposComprobante, tipoComprobante]
  );
  const esEspecial = useMemo(() => {
    if (documentoSeleccionado && String(documentoSeleccionado.codigo) === "44") return true;
    return (tipoComprobante || "").toLowerCase().includes("especial");
  }, [documentoSeleccionado, tipoComprobante]);

  // Documento tipo E32 (Factura de Consumo Electrónica).
  const esE32 = useMemo(() => {
    if (!documentoSeleccionado) return false;
    const encabezado = (documentoSeleccionado.nombre || "").split(":")[1]?.trim().toUpperCase() || "";
    const ncf = (documentoSeleccionado.comprobantefiscal || "").toUpperCase();
    return (
      encabezado.startsWith("E32") ||
      ncf.startsWith("E32") ||
      String(documentoSeleccionado.codigo) === "32"
    );
  }, [documentoSeleccionado]);

  // ------- Cargar tipos de documento fiscal desde la tabla tipos_documentos_fiscales (backend) -------
  const cargarTiposComprobante = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + "/api/facturaselectronicasdgii_listartiposcomprobante", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuario: usuarioLog || "", password: claveLog || "", empresa: empresaFiscal }),
      });
      if (response.ok) {
        const data = await response.json();
        const tiposActivos = (data.tipos || []).filter(
          (tipo) =>
            tipo.activo === "S" &&
            CODIGOS_DOCUMENTO_DGII.includes(codigoTipoDocumento(tipo))
        );
        setTiposComprobante(tiposActivos);
        if (tiposActivos.length > 0) {
          setTipoComprobante((prev) => prev || tiposActivos[0].nombre);
        }
      }
    } catch {
      /* Sin tipos: el select quedara vacio */
    }
  }, [servidor, usuarioLog, claveLog]);

  useEffect(() => {
    cargarTiposComprobante();
  }, [cargarTiposComprobante]);

  const idEmpresaMotor = useMemo(
    () => (selectedCompany?.db_empresa ? selectedCompany : empresaProductosDesdeContexto(nombreNegocio)),
    [selectedCompany, nombreNegocio]
  );

  const cargarFormasPago = useCallback(async () => {
    if (!usuarioLog || !idEmpresaMotor?.db_empresa || !moduloContabilidadInstalado(modulos)) {
      setFormasPagoDb([]);
      return;
    }
    try {
      const response = await fetch(`${servidor}/configuracionbackend_listarformasdepago`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_empresa: idEmpresaMotor,
          usuario: usuarioLog || "",
          password: claveLog || "", empresa: empresaFiscal,
        }),
      });
      const data = await response.json().catch(() => ({}));
      setFormasPagoDb(response.ok ? data.formas_pago || [] : []);
    } catch {
      setFormasPagoDb([]);
    }
  }, [idEmpresaMotor, usuarioLog, claveLog, servidor, modulos]);

  const enviarAlMotorContableVentasContado = useCallback(
    async (ctxDocumento) => {
      if (!debeEnviarAlMotor(modulos, enviarMotorContable)) return;
      if (!idEmpresaMotor?.db_empresa) return;
      try {
        const tipoDoc = TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO;
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_empresa: idEmpresaMotor,
            usuario: usuarioLog || "",
            password: claveLog || "", empresa: empresaFiscal,
            tipodocumento: tipoDoc,
            ...payloadFormaPagoSiAplica(tipoDoc, ctxDocumento.formaPago),
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || "No se pudieron consultar las reglas del motor contable");
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(reglasData.registros, tipoDoc);
        if (!documentoMotorDefinido(reglasDoc, tipoDoc)) {
          showWarning(
            "No hay reglas del motor para Ventas Contado; no se registró en diario/histdia"
          );
          return;
        }
        const payloadDocumento = construirDocumentoMotorVentasContado(reglasDoc, ctxDocumento);
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_empresa: idEmpresaMotor,
            usuario: usuarioLog || "",
            password: claveLog || "", empresa: empresaFiscal,
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || "No se pudo enviar la venta al motor contable");
          return;
        }
        if (motorData.omitido) return;
        if (motorData.ok) {
          if (motorData.descuadrado) {
            showWarning(motorData.mensaje || "Motor contable: asiento guardado descuadrado");
          } else {
            showSuccess(motorData.mensaje || "Motor contable: asiento generado");
          }
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning("Error de conexión al enviar la venta al motor contable");
      }
    },
    [
      modulos,
      enviarMotorContable,
      servidor,
      idEmpresaMotor,
      usuarioLog,
      claveLog,
      showWarning,
      showSuccess,
    ]
  );

  useEffect(() => {
    cargarFormasPago();
  }, [cargarFormasPago]);

  // El documento fiscal seleccionado genera automaticamente el comprobante fiscal
  // (eNCF), tal como el backend de FacturaPOS produce el NCF a partir del documento.
  useEffect(() => {
    if (documentoSeleccionado && documentoSeleccionado.comprobantefiscal) {
      setComprobante(documentoSeleccionado.comprobantefiscal);
    }
  }, [documentoSeleccionado]);

  // Autocomplete RNC (mismo endpoint /api/facturabackend_buscarrnc que FacturaPOS)
  const buscarRncs = useCallback(
    async (busqueda = "") => {
      if (!usuarioLog) return;
      setCargandoRnc(true);
      try {
        const selectedCompany = empresaProductosDesdeContexto(nombreNegocio);
        const response = await fetch(servidor + "/api/facturabackend_buscarrnc", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || "",
            password: claveLog || "", empresa: empresaFiscal,
            busqueda: (busqueda || "").trim(),
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setOpcionesRnc(data.rncs || []);
        }
      } catch {
        // Silencioso: el campo sigue usable como texto libre.
      } finally {
        setCargandoRnc(false);
      }
    },
    [servidor, usuarioLog, claveLog, nombreNegocio]
  );

  // ------- Cargar chasis saldados desde la tabla de prestamos -------
  const buscarChasisSaldados = useCallback(
    async (busqueda = "") => {
      if (!usuarioLog) return;
      setCargandoChasis(true);
      try {
        const response = await fetch(servidor + "/api/facturaselectronicasdgii_buscarsaldados", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            usuario: usuarioLog || "",
            password: claveLog || "", empresa: empresaFiscal,
            busqueda,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setOpcionesChasis(data.chasis || []);
        } else {
          setOpcionesChasis([]);
        }
      } catch {
        setOpcionesChasis([]);
      } finally {
        setCargandoChasis(false);
      }
    },
    [servidor, usuarioLog, claveLog]
  );

  useEffect(() => {
    if (modoOrigen === "saldados") {
      buscarChasisSaldados("");
    }
  }, [buscarChasisSaldados, modoOrigen]);

  // Busqueda con debounce mientras se escribe en el autocomplete
  useEffect(() => {
    if (modoOrigen !== "saldados") return undefined;
    const t = setTimeout(() => {
      buscarChasisSaldados(inputChasis || "");
    }, 350);
    return () => clearTimeout(t);
  }, [inputChasis, buscarChasisSaldados, modoOrigen]);

  const buscarFacturasMayor = useCallback(
    async (busqueda = "") => {
      if (!usuarioLog) return;
      setCargandoMayor(true);
      try {
        const response = await fetch(servidor + "/api/facturaselectronicasdgii_buscarxmayor", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            usuario: usuarioLog || "",
            password: claveLog || "", empresa: empresaFiscal,
            busqueda,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setFacturasMayor(data.facturas || []);
        } else {
          setFacturasMayor([]);
        }
      } catch {
        setFacturasMayor([]);
      } finally {
        setCargandoMayor(false);
      }
    },
    [servidor, usuarioLog, claveLog]
  );

  useEffect(() => {
    if (modoOrigen === "mayor") {
      buscarFacturasMayor("");
    }
  }, [buscarFacturasMayor, modoOrigen]);

  useEffect(() => {
    if (modoOrigen !== "mayor") return undefined;
    const t = setTimeout(() => {
      buscarFacturasMayor(inputMayor || "");
    }, 350);
    return () => clearTimeout(t);
  }, [inputMayor, buscarFacturasMayor, modoOrigen]);

  useEffect(
    () => () => {
      if (busquedaRncTimerRef.current) {
        clearTimeout(busquedaRncTimerRef.current);
      }
    },
    []
  );

  // ------- Totales (replica el calculo del e-CF de fac1030) -------
  const totales = useMemo(() => {
    const totalImporte = lineasFactura.reduce(
      (sum, it) => sum + toNumber(it.cantidad) * toNumber(it.precio),
      0
    );
    const descGen = toNumber(descuentoGeneral);

    let subtotal = 0;
    let descLineas = 0;
    let itbis = 0;
    let gravado = 0;
    let exento = 0;

    lineasFactura.forEach((it) => {
      const cant = toNumber(it.cantidad);
      const val = toNumber(it.precio);
      const descLinea = toNumber(it.descuento);
      const tasa = toNumber(it.tasaitbis);
      const importe = cant * val;
      const descProrrateado = totalImporte ? descGen * (importe / totalImporte) : 0;
      const base = importe - descLinea - descProrrateado;

      subtotal += importe;
      descLineas += descLinea;

      if (esEspecial || tasa === 0) {
        exento += base;
      } else {
        gravado += base;
        itbis += base * (tasa / 100);
      }
    });

    const env = toNumber(envio);
    const otros = toNumber(otrosCargos);
    const descTotal = descLineas + descGen;
    const totalGeneral = subtotal - descTotal + itbis + env + otros;

    return { subtotal, descTotal, gravado, exento, itbis, env, otros, totalGeneral };
  }, [lineasFactura, descuentoGeneral, envio, otrosCargos, esEspecial]);

  // La DGII exige identificar al comprador (RNC o cédula) en facturas de
  // consumo (E32) de RD$250,000 o más; por debajo el consumo va anónimo.
  const LIMITE_CONSUMO_SIN_RNC = 250000;
  const e32RequiereRnc = esE32 && totales.totalGeneral >= LIMITE_CONSUMO_SIN_RNC;
  const rncComprador = esE32 && !e32RequiereRnc ? "" : rnc.trim();
  const razonSocialComprador = esE32 && !e32RequiereRnc ? "" : razonSocial.trim();

  useEffect(() => {
    if (!esE32 || e32RequiereRnc) {
      buscarRncs("");
    }
  }, [esE32, e32RequiereRnc, buscarRncs]);

  // ------- Manejo de items -------
  const idFacturaMayor = (chasis) => {
    const valor = chasis?.facturaMayor ?? chasis?.factura ?? null;
    return valor == null || valor === "" ? null : String(valor);
  };

  const facturaMayorEnGrid = useMemo(() => {
    const item = itemsFactura.find((it) => it.facturaMayor != null && it.facturaMayor !== "");
    return item ? String(item.facturaMayor) : null;
  }, [itemsFactura]);

  const mismaFacturaMayorOVacio = (facturaId) => {
    if (facturaMayorEnGrid == null) return true;
    if (facturaId == null || facturaId === "") return true;
    return String(facturaMayorEnGrid) === String(facturaId);
  };

  const itemDesdeChasis = (chasis) => ({
    id: Date.now() + Math.random(),
    key: `${chasis.id}-${chasis.chasis}`,
    // Las ventas al contado traen como id la factura (factmot), no un préstamo.
    noprest: chasis.origen === "contado" ? null : chasis.id,
    chasis: chasis.chasis,
    facturaMayor: idFacturaMayor(chasis),
    descripcion: `${chasis.marca || ""} ${chasis.modelo || ""} - CHASIS ${chasis.chasis || ""}`.trim(),
    // Dueño del chasis (cliente de la venta), editable; viaja como sub-descripción.
    dueno: String(chasis.nombres || "").trim(),
    cantidad: 1,
    precio: toNumber(chasis.precioraw ?? chasis.precio),
    descuento: 0,
    tasaitbis: esEspecial ? "0" : "18",
  });

  const agregarChasis = (chasis) => {
    if (!chasis) return;
    const key = `${chasis.id}-${chasis.chasis}`;
    if (itemsFactura.some((it) => it.key === key)) {
      alert("Ese chasis ya fue agregado a la factura");
      return;
    }
    const facturaOrigen = idFacturaMayor(chasis);
    if (facturaOrigen && !mismaFacturaMayorOVacio(facturaOrigen)) {
      alert(
        `Ya hay chasis de la factura x mayor #${facturaMayorEnGrid}. No puede agregar una factura diferente.`
      );
      return;
    }
    setItemsFactura((prev) => [...prev, itemDesdeChasis(chasis)]);
    setInputChasis("");
  };

  const agregarChasisFactura = (factura) => {
    const lista = factura?.chasis || [];
    if (lista.length === 0) return;
    if (!mismaFacturaMayorOVacio(factura.id)) {
      alert(
        `Ya hay chasis de la factura x mayor #${facturaMayorEnGrid}. No puede agregar una factura diferente.`
      );
      return;
    }
    const keys = new Set(itemsFactura.map((it) => it.key));
    const nuevos = lista
      .filter((ch) => ch?.chasis && !keys.has(`${ch.id}-${ch.chasis}`))
      .map((ch) => itemDesdeChasis(ch));
    if (nuevos.length === 0) {
      alert("Todos los chasis de esa factura ya están en la factura electrónica");
      return;
    }
    setItemsFactura((prev) => [...prev, ...nuevos]);
  };

  const eliminarItem = (id) => setItemsFactura((prev) => prev.filter((it) => it.id !== id));

  // Activa la línea de placa con el último monto fijo usado por este usuario.
  const agregarPlaca = () => {
    if (!montoPlaca) setMontoPlaca(leerMontoPlaca(usuarioLog));
    setIncluirPlaca(true);
  };

  const actualizarItem = (id, campo, valor) =>
        setItemsFactura((prev) => prev.map((it) => (it.id === id ? { ...it, [campo]: valor } : it)));

  const actualizarCantidad = (id, nuevaCantidad) => {
    if (nuevaCantidad <= 0) {
      eliminarItem(id);
      return;
    }
    actualizarItem(id, "cantidad", nuevaCantidad);
  };

  // Restablece el formulario a su estado inicial (sin confirmacion). Se usa tras
  // guardar la factura para dejar la pantalla lista para una nueva.
  const resetFormulario = () => {
    setItemsFactura([]);
    setIncluirPlaca(false);
    setRnc("");
    setRazonSocial("");
    setGarantia("");
    setEnvio("0.00");
    setOtrosCargos("0.00");
    setDescuentoGeneral("0.00");
  };

  const limpiarFactura = () => {
    if (window.confirm("¿Está seguro de limpiar toda la factura?")) {
      resetFormulario();
      cargarTiposComprobante();
    }
  };

  const manejarGuardarClick = () => {
    if (!tipoComprobante) {
      alert("Debe seleccionar un tipo de documento fiscal");
      return;
    }
    if (!esE32 || e32RequiereRnc) {
      const motivo = e32RequiereRnc
        ? "(requerido: consumo de RD$250,000 o más)"
        : "";
      if (!rnc.trim()) {
        alert(`Debe ingresar el RNC / Cédula del cliente ${motivo}`);
        return;
      }
      if (!razonSocial.trim()) {
        alert(`Debe ingresar la razón social / nombre del cliente ${motivo}`);
        return;
      }
    }
    if (!comprobante.trim()) {
      alert("Ingrese el comprobante fiscal (eNCF)");
      return;
    }
    if (itemsFactura.length === 0) {
      alert("Agregue al menos un chasis a la factura");
      return;
    }
    if (incluirPlaca) {
      if (!(toNumber(montoPlaca) > 0)) {
        alert("Indique el monto fijo de la placa por chasis");
        return;
      }
      recordarMontoPlaca(usuarioLog, toNumber(montoPlaca));
    }
    setDialogoPagoAbierto(true);
  };

  // Mapea el detalle de la factura electronica al body que espera el endpoint de
  // guardado (facturaselectronicadgii_guardarfactura), prorrateando el descuento
  // general por linea igual que FacturaPOS.
  const construirBodyGuardado = (datosPago) => {
    const selectedCompany = empresaProductosDesdeContexto(nombreNegocio);
    const itemsBase = lineasFactura.map((it) => {
      const cantidad = toNumber(it.cantidad);
      const precio = toNumber(it.precio);
      const descuento = toNumber(it.descuento);
      const tasa = esEspecial ? 0 : toNumber(it.tasaitbis);
      const lineSub = Math.max(0, cantidad * precio - descuento);
      return { it, cantidad, precio, descuento, tasa, lineSub };
    });
    const sumaLineSub = itemsBase.reduce((s, b) => s + b.lineSub, 0);
    const descGeneralTotal = toNumber(descuentoGeneral);

    const itemsGuardar = itemsBase.map((b) => {
      const descGenItem = sumaLineSub > 0 ? descGeneralTotal * (b.lineSub / sumaLineSub) : 0;
      return {
        codigo: b.it.esPlaca ? CODIGO_PLACA : b.it.chasis || b.it.noprest || "",
        descripcion: b.it.descripcion,
        cantidad: b.cantidad,
        precio: b.precio,
        descuento: b.descuento,
        descuento_general: descGenItem,
        impuesto: b.tasa,
        subtotal: Math.max(0, b.lineSub - descGenItem),
        tipo: b.it.esPlaca ? "cargo" : "producto",
        chasis: b.it.chasis,
        noprest: b.it.noprest,
        subdescripcion: subdescripcionItem(b.it),
      };
    });

    const subtotalCalc = itemsBase.reduce((s, b) => s + b.cantidad * b.precio, 0);
    const descuentoLineasCalc = itemsBase.reduce((s, b) => s + b.descuento, 0);
    // ITBIS de toda la línea (cantidad × precio menos descuentos), redondeado
    // por línea igual que el backend al guardar.
    const impuestosCalc = itemsGuardar.reduce(
      (s, it) => s + redondearDinero2(it.subtotal * (it.impuesto / 100)),
      0
    );
    const totalCalc =
      subtotalCalc - descuentoLineasCalc - descGeneralTotal + impuestosCalc + toNumber(envio) + toNumber(otrosCargos);
    const fechaMotor = hoyLocal();
    const enviarMotor = debeEnviarAlMotor(modulos, enviarMotorContable);
    const tipoDocMotor = TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO;
    const documentoFormaPago = enviarMotor
      ? documentoFormaPagoDesdePago(datosPago, formasPagoDb)
      : "";
    const lineasMotor = enviarMotor ? lineasMotorDesdePago(datosPago, formasPagoDb) : [];
    const montosMotor = enviarMotor
      ? {
          ingresos: redondearDinero2(subtotalCalc),
          itbis: redondearDinero2(esEspecial ? 0 : impuestosCalc),
          descuentos: redondearDinero2(descuentoLineasCalc + descGeneralTotal),
          cajageneral: redondearDinero2(totalCalc),
        }
      : null;
    const payloadMotor = enviarMotor
      ? {
          modulos,
          tipodocumento: tipoDocMotor,
          ...payloadFormaPagoSiAplica(tipoDocMotor, documentoFormaPago),
          lineas_motor: lineasMotor,
          montos: montosMotor,
        }
      : {};
    const motoraguardar = "S";
    return {
      body: {
        id_empresa: selectedCompany,
        usuario: usuarioLog || "",
        password: claveLog || "", empresa: empresaFiscal,
        anomesfacturacion: selectedCompany?.anomesfacturacion,
        localidad: localStorage.getItem("sucursal") || null,
        motoraguardar: motoraguardar,
        ...payloadMotor,
        factura: {
          cliente: rncComprador,
          razonsocial: razonSocialComprador,
          items: itemsGuardar,
          impuestos: [],
          subtotal: subtotalCalc - descGeneralTotal,
          total_descuentos: descuentoLineasCalc + descGeneralTotal,
          total_impuestos: impuestosCalc,
          total: totalCalc,
          ncf: "",
          tipo_documento: tipoComprobante,
          tipo_factura: "FACTURA ELECTRONICA",
          fecha: fechaMotor,
          usuario: usuarioLog,
          observaciones: garantia.trim(),
          pago: datosPago || null,
          cotizacion_id: null,
          comprobanteelectronico: comprobanteelectronico === "S" ? "S" : "N",
          modulosasignados: modulos,
          ...(enviarMotor
            ? {
                tipodocumento: tipoDocMotor,
                ...payloadFormaPagoSiAplica(tipoDocMotor, documentoFormaPago),
              }
            : {}),
        },
      },
      motor: enviarMotor
        ? {
            documentoFormaPago,
            lineasMotor,
            montosMotor,
            fechaMotor,
            totalCalc,
          }
        : null,
    };
  };

  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "factura_ecf.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const descargarXmlECF = (xmlTexto, filename) => {
    descargarArchivoECF(new Blob([xmlTexto], { type: "text/xml" }), filename);
  };

  const generarFactura = async (datosPago) => {
    setGenerando(true);

    const { body, motor } = construirBodyGuardado(datosPago);
    const { ok, data, error } = await guardarFacturaBackend(servidor, body);
    if (!ok) {
      setGenerando(false);
      showError(error || "Error al guardar la factura");
      return;
    }

    // Refresca el catálogo RNC por si el backend acaba de registrar uno nuevo.
    buscarRncs("");

    const comprobantefiscalGuardado = data?.comprobantefiscal || comprobante.trim();

    const payload = {
      comprobanteelectronico,
      rnc: rncComprador,
      razonsocial: razonSocialComprador,
      comprobante: comprobantefiscalGuardado,
      tipocomprobante: tipoComprobante,
      codigocomprobante: documentoSeleccionado?.codigo ?? "",
      fechavencimiento: documentoSeleccionado?.valido ?? "",
      garantia: garantia.trim(),
      envio: toNumber(envio),
      otroscargos: toNumber(otrosCargos),
      descuentogeneral: toNumber(descuentoGeneral),
      pago: datosPago || null,
      items: lineasFactura.map((it) => ({
        item: it.descripcion,
        cantidad: toNumber(it.cantidad),
        valor: toNumber(it.precio),
        descuento: toNumber(it.descuento),
        tasaitbis: esEspecial ? 0 : toNumber(it.tasaitbis),
        chasis: it.esPlaca ? CODIGO_PLACA : it.chasis,
        noprest: it.noprest,
        subdescripcion: it.esPlaca ? "" : subdescripcionItem(it),
      })),
      password: claveLog, empresa: empresaFiscal,
      usuario: usuarioLog,
    };

    let fechaFirma = "";
    let codigoSeguridad = "";
    let montoTotalEcf = null;
    let ambienteEcf = "";

    if (comprobanteelectronico === "S") {
      const ecf = await enviarPayloadFacturaElectronica(servidor, payload);
      if (ecf.ok) {
        descargarArchivoECF(ecf.blob, ecf.filename);
      } else {
        showError(ecf.error || "No se pudo generar el comprobante electrónico");
      }

      const emision = await emitirEcfDgii(servidor, {
        ...payload,
        rncemisor: emisorFiscal.rnc || "",
        razonsocialemisor: emisorFiscal.nombre || "",
        direccionemisor: emisorFiscal.direccion || "",
      });
      const estadoEcf = String(emision?.estado || "").trim().toLowerCase();
      const dgiiFallido = estadoEcf === "rechazado" || estadoEcf === "error";

      const revertirPorEcfFallido = async () => {
        const rev = await revertirFacturaRechazada(servidor, {
          id_empresa: selectedCompany,
          usuario: usuarioLog || "",
          password: claveLog || "", empresa: empresaFiscal,
          nfact: data.numero,
          tipo_documento: data.documento || tipoComprobante,
          anomesfacturacion: selectedCompany?.anomesfacturacion || "productosnoserializados",
          motoraguardar: "S",
          comprobante: comprobantefiscalGuardado,
          ncf: comprobantefiscalGuardado,
        });
        if (rev.ok) {
          showError(
            `Factura ${data.documento}-${data.numero} revertida automáticamente (e-CF ${estadoEcf || "error"})`
          );
        } else {
          showError(rev.error || "No se pudo revertir la factura rechazada");
        }
        resetFormulario();
        await cargarTiposComprobante();
      };

      if (emision.ok) {
        descargarXmlECF(emision.xml, `${emision.encf || "ecf"}.xml`);
        if (emision.xml_rfce) {
          descargarXmlECF(emision.xml_rfce, `${emision.encf || "ecf"}-resumen.xml`);
        }
        fechaFirma = emision.fecha_firma || "";
        ambienteEcf = emision.ambiente || "";
        codigoSeguridad = emision.codigo_seguridad || "";
        montoTotalEcf = emision.monto_total ?? null;
        const etiquetas = {
          aceptado: "aceptado por la DGII",
          aceptado_condicional: "aceptado condicional por la DGII",
          en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ""}`,
          rechazado: "RECHAZADO por la DGII",
          pendiente_envio: "firmado; DGII inaccesible, se reenviará",
          error: "firmado; el envío a la DGII falló",
        };
        const detalleDgii = (emision.respuesta_dgii?.mensajes || [])
          .map((m) => `${m.valor || m.mensaje || ""}${m.codigo ? ` (${m.codigo})` : ""}`)
          .filter((t) => t.trim())
          .join(" · ");
        const mensaje =
          `e-CF ${emision.encf} ${etiquetas[estadoEcf] || emision.estado}` +
          ` · código ${emision.codigo_seguridad}` +
          (detalleDgii ? ` — DGII: ${detalleDgii}` : "");
        if (dgiiFallido) {
          showError(mensaje);
          await revertirPorEcfFallido();
          setGenerando(false);
          return;
        }
        showSuccess(mensaje);
      } else {
        showError(emision.error || "No se pudo emitir el e-CF");
        await revertirPorEcfFallido();
        setGenerando(false);
        return;
      }
    }

    if (motor) {
      const ctxMotor = {
        valorTotal: redondearDinero2(motor.totalCalc),
        formaPago: motor.documentoFormaPago,
        lineas: motor.lineasMotor,
        nfactura: String(data.numero || ""),
        documento: data.documento || tipoComprobante || "FACTURA ELECTRONICA",
        numero: data.numero,
        fecha: motor.fechaMotor,
        tasa: 1,
        usuario: usuarioLog || "",
        concepto: `Venta contado ${data.documento || tipoComprobante}-${data.numero}`,
        montos: motor.montosMotor,
      };
      const motorSave = data.motor;
      if (motorSave?.registrado) {
        if (motorSave.descuadrado) {
          showWarning(motorSave.mensaje || "Motor contable: asiento guardado descuadrado");
        } else {
          showSuccess(motorSave.mensaje || "Motor contable: asiento registrado en diario e histdia");
        }
      } else if (motorSave?.omitido) {
        // Sin reglas: no se reenvía.
      } else if (motorSave) {
        if (motorSave.mensaje) showWarning(motorSave.mensaje);
      } else {
        await enviarAlMotorContableVentasContado(ctxMotor);
      }
    }

    setGenerando(false);
    // Misma vista e impresión que el POS: el formato es el configurado para
    // el documento en Configuración de factura (80mm, carta, etc.).
    setDatosImpresion(
      datosVistaFactura({
        payload,
        totales,
        numero: data?.numero,
        documento: data?.documento || tipoComprobante,
        tipoDocumento: documentoSeleccionado?.nombre || tipoComprobante,
        idEmpresa: idEmpresaMotor,
        emisor: emisorFiscal,
        fechaFirma,
        codigoSeguridad,
        montoTotalEcf,
        ambiente: ambienteEcf,
        usuario: usuarioLog,
      })
    );
    setDialogoImpresionAbierto(true);
  };

  const opcionLabel = (opt) =>
    opt ? `${opt.chasis} — ${opt.marca || ""} ${opt.modelo || ""} · ${opt.nombres || ""}`.trim() : "";

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Facturas Electronicas DGII"
        subtitle="Facturación de chasis saldados y generación del e-CF"
        kicker="Facturación"
      />

      <Grid container spacing={2} sx={{ flex: 1, mt: 0.5 }}>
        {/* ------- Columna izquierda: busqueda de chasis (tabla prestamo) ------- */}
        <Grid item xs={12} md={5}>
          <Card sx={{ ...cardShellSx, display: "flex", flexDirection: "column", minHeight: "600px" }}>
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                p: 2.5,
                minHeight: 0,
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 2, pb: 1.2, borderBottom: `1px solid ${FP.color.border}`, width: "100%" }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        borderRadius: 1.2,
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.accentBlue,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Inventory2Icon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
                      {modoOrigen === "mayor" ? "Agregar Producto — x Mayor" : "Agregar Producto — Saldados y Contado"}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant={modoOrigen === "mayor" ? "contained" : "outlined"}
                    startIcon={<StorefrontIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setModoOrigen((prev) => (prev === "mayor" ? "saldados" : "mayor"))}
                    sx={{
                      flexShrink: 0,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 1.4,
                      px: 1.4,
                      minWidth: 0,
                      boxShadow: "none",
                      borderColor: modoOrigen === "mayor" ? FP.color.navy : FP.color.border,
                      bgcolor: modoOrigen === "mayor" ? FP.color.navy : "#FFFFFF",
                      color: modoOrigen === "mayor" ? "#FFFFFF" : FP.color.textPrimary,
                      "&:hover": {
                        boxShadow: "none",
                        bgcolor: modoOrigen === "mayor" ? FP.color.navyDeep : FP.color.surfaceAlt,
                        borderColor: FP.color.borderStrong,
                      },
                    }}
                  >
                    Mayor
                  </Button>
                </Stack>

                {modoOrigen === "mayor" ? (
                  <TextField
                    fullWidth
                    value={inputMayor}
                    onChange={(e) => setInputMayor(e.target.value)}
                    placeholder="Buscar factura, chasis, cliente o RNC…"
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2, ...fpInputSx }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: FP.color.textMuted }} />
                        </InputAdornment>
                      ),
                      endAdornment: cargandoMayor ? (
                        <InputAdornment position="end">
                          <CircularProgress color="inherit" size={16} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                ) : (
                  <Autocomplete
                    fullWidth
                    options={opcionesChasis}
                    loading={cargandoChasis}
                    value={null}
                    blurOnSelect
                    clearOnBlur
                    inputValue={inputChasis}
                    onInputChange={(e, v) => setInputChasis(v)}
                    onChange={(e, v) => agregarChasis(v)}
                    getOptionLabel={opcionLabel}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id && opt?.chasis === val?.chasis}
                    noOptionsText={inputChasis ? "Sin resultados saldados ni de contado" : "Escriba para buscar chasis"}
                    renderOption={(props, opt) => (
                      <li {...props} key={`${opt.id}-${opt.chasis}`}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.85rem" }}>
                            {opt.chasis}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem" }}>
                            {opt.origen === "contado" ? `Contado #${opt.id} · ` : ""}{opt.marca} {opt.modelo} · {opt.nombres} · ${formatMoney(opt.precioraw ?? opt.precio)} · ${formatMoney(opt.preciomin ?? 0)}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar chasis, cliente o cédula…"
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2, ...fpInputSx }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: FP.color.textMuted }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {cargandoChasis ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              </Box>

              <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                {modoOrigen === "mayor" ? (
                  cargandoMayor && facturasMayor.length === 0 ? (
                    <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                      Cargando facturas x mayor…
                    </Typography>
                  ) : facturasMayor.length === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                      <StorefrontIcon sx={{ fontSize: 52, color: FP.color.textMuted, mb: 1.5 }} />
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                        No hay facturas x mayor con chasis pendientes de e-CF.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.4}>
                      {facturasMayor.map((factura) => {
                        const pendientes = (factura.chasis || []).filter(
                          (ch) => !itemsFactura.some((it) => it.key === `${ch.id}-${ch.chasis}`)
                        );
                        const otraFactura = !mismaFacturaMayorOVacio(factura.id);
                        return (
                          <Box
                            key={factura.id}
                            sx={{
                              border: `1px solid ${FP.color.border}`,
                              borderRadius: 2,
                              p: 1.2,
                              bgcolor: FP.color.surfaceAlt,
                              opacity: otraFactura ? 0.55 : 1,
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.82rem" }}>
                                  Factura #{factura.id}
                                  {factura.nfactura ? ` · ${factura.nfactura}` : ""}
                                </Typography>
                                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.72rem" }} noWrap>
                                  {factura.fecha} · {factura.nombres || "Sin cliente"}
                                  {factura.rnc ? ` · ${factura.rnc}` : ""}
                                  {` · ${(factura.chasis || []).length} chasis`}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={pendientes.length === 0 || otraFactura}
                                onClick={() => agregarChasisFactura(factura)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  borderRadius: 1.2,
                                  whiteSpace: "nowrap",
                                  borderColor: FP.color.border,
                                  color: FP.color.textPrimary,
                                  bgcolor: "#FFFFFF",
                                  "&:hover": { borderColor: FP.color.borderStrong, bgcolor: "#FFFFFF" },
                                }}
                              >
                                {otraFactura ? "Otra factura" : pendientes.length === 0 ? "Agregados" : "Agregar todos"}
                              </Button>
                            </Stack>
                            <Grid container spacing={1.2}>
                              {(factura.chasis || []).map((opt) => {
                                const yaAgregado = itemsFactura.some((it) => it.key === `${opt.id}-${opt.chasis}`);
                                const noAgregar = yaAgregado || otraFactura;
                                return (
                                  <Grid item xs={12} sm={6} key={`${opt.id}-${opt.chasis}`}>
                                    <Card
                                      onClick={() => !noAgregar && agregarChasis(opt)}
                                      sx={{
                                        cursor: noAgregar ? "not-allowed" : "pointer",
                                        borderRadius: 2,
                                        transition: "all .15s ease",
                                        bgcolor: FP.color.cardBg,
                                        border: `1px solid ${yaAgregado ? FP.color.accentBlue : FP.color.border}`,
                                        boxShadow: "none",
                                        opacity: noAgregar ? 0.7 : 1,
                                        "&:hover": noAgregar
                                          ? {}
                                          : {
                                              borderColor: FP.color.accentBlue,
                                              boxShadow: FP.shadow.soft,
                                              transform: "translateY(-2px)",
                                            },
                                      }}
                                    >
                                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                        <Typography
                                          sx={{
                                            color: FP.color.textMuted,
                                            fontWeight: 600,
                                            fontSize: "0.68rem",
                                            letterSpacing: "0.04em",
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          #{factura.id} · {opt.fecha}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            color: FP.color.textPrimary,
                                            fontSize: "0.82rem",
                                            wordBreak: "break-all",
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          {opt.chasis}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: FP.color.textSecondary,
                                            fontSize: "0.74rem",
                                            mb: 0.5,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                          }}
                                        >
                                          {opt.marca} {opt.modelo} · {opt.nombres}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: FP.color.navy,
                                            fontWeight: 800,
                                            fontSize: "0.95rem",
                                            fontVariantNumeric: "tabular-nums",
                                          }}
                                        >
                                          ${formatMoney(opt.precioraw ?? opt.precio)}
                                        </Typography>
                                        <Chip
                                          label={yaAgregado ? "Agregado" : otraFactura ? "Otra factura" : "x Mayor"}
                                          size="small"
                                          sx={{
                                            mt: 0.6,
                                            fontWeight: 700,
                                            fontSize: "0.66rem",
                                            bgcolor: yaAgregado
                                              ? FP.color.accentBlueSoft
                                              : otraFactura
                                                ? FP.color.warningSoft
                                                : FP.color.infoSoft,
                                            color: yaAgregado
                                              ? FP.color.accentBlue
                                              : otraFactura
                                                ? FP.color.warning
                                                : FP.color.info,
                                          }}
                                        />
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        );
                      })}
                    </Stack>
                  )
                ) : cargandoChasis && opcionesChasis.length === 0 ? (
                  <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                    Cargando chasis saldados…
                  </Typography>
                ) : opcionesChasis.length === 0 ? (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                    <TwoWheelerIcon sx={{ fontSize: 52, color: FP.color.textMuted, mb: 1.5 }} />
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                      No hay chasis saldados ni de contado que coincidan con la búsqueda.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={1.2}>
                    {opcionesChasis.map((opt) => {
                      const yaAgregado = itemsFactura.some((it) => it.key === `${opt.id}-${opt.chasis}`);
                      return (
                        <Grid item xs={12} sm={6} key={`${opt.id}-${opt.chasis}`}>
                          <Card
                            onClick={() => !yaAgregado && agregarChasis(opt)}
                            sx={{
                              cursor: yaAgregado ? "not-allowed" : "pointer",
                              borderRadius: 2,
                              transition: "all .15s ease",
                              bgcolor: FP.color.cardBg,
                              border: `1px solid ${yaAgregado ? FP.color.accentBlue : FP.color.border}`,
                              boxShadow: "none",
                              opacity: yaAgregado ? 0.7 : 1,
                              "&:hover": yaAgregado
                                ? {}
                                : {
                                    borderColor: FP.color.accentBlue,
                                    boxShadow: FP.shadow.soft,
                                    transform: "translateY(-2px)",
                                  },
                            }}
                          >
                            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                              <Typography
                                sx={{
                                  color: FP.color.textMuted,
                                  fontWeight: 600,
                                  fontSize: "0.68rem",
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                }}
                              >
                                {opt.origen === "contado" ? "Contado " : ""}#{opt.id} · {opt.fecha}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: FP.color.textPrimary,
                                  fontSize: "0.82rem",
                                  wordBreak: "break-all",
                                  lineHeight: 1.3,
                                }}
                              >
                                {opt.chasis}
                              </Typography>
                              <Typography
                                sx={{
                                  color: FP.color.textSecondary,
                                  fontSize: "0.74rem",
                                  mb: 0.5,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {opt.marca} {opt.modelo} · {opt.nombres}
                              </Typography>
                              <Typography
                                sx={{
                                  color: FP.color.navy,
                                  fontWeight: 800,
                                  fontSize: "0.95rem",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                ${formatMoney(opt.precioraw ?? opt.precio)}
                              </Typography>
                              <Chip
                                label={yaAgregado ? "Agregado" : "Saldado"}
                                size="small"
                                sx={{
                                  mt: 0.6,
                                  fontWeight: 700,
                                  fontSize: "0.66rem",
                                  bgcolor: yaAgregado ? FP.color.accentBlueSoft : FP.color.successSoft,
                                  color: yaAgregado ? FP.color.accentBlue : FP.color.successDark,
                                }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ------- Columna derecha: factura ------- */}
        <Grid item xs={12} md={7}>
          <Card sx={{ ...cardShellSx, display: "flex", flexDirection: "column", minHeight: "600px" }}>
            <CardContent sx={{ display: "flex", flexDirection: "column", p: 2.5 }}>
              {/* Datos fiscales */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: FP.color.surfaceAlt,
                  border: `1px solid ${FP.color.border}`,
                  flexShrink: 0,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      fullWidth
                      options={tiposComprobante}
                      value={documentoSeleccionado}
                      onChange={(e, v) => {
                        setTipoComprobante(v ? v.nombre : "");
                        setItemsFactura([]);
                      }}
                      getOptionLabel={(opt) => opt?.nombre ?? ""}
                      isOptionEqualToValue={(opt, val) => opt?.nombre === val?.nombre}
                      disabled={tiposComprobante.length === 0}
                      noOptionsText="No hay documentos fiscales"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Documento fiscal"
                          required
                          InputLabelProps={{ shrink: true }}
                          helperText={
                            tiposComprobante.length === 0
                              ? "No hay tipos de documentos configurados"
                              : "Seleccione el tipo de documento"
                          }
                          sx={fpInputSx}
                        />
                      )}
                    />
                  </Grid>
                  {(!esE32 || e32RequiereRnc) && (
                    <>
                      <Grid item xs={12} sm={5}>
                        <Autocomplete
                          freeSolo
                          options={opcionesRnc}
                          loading={cargandoRnc}
                          value={rnc}
                          getOptionLabel={(option) => {
                            if (typeof option === "string") return option;
                            const codigo = (option?.rnc || "").trim();
                            const nombre = (option?.descripcion || "").trim();
                            if (codigo && nombre) return `${codigo} — ${nombre}`;
                            return codigo || nombre || "";
                          }}
                          isOptionEqualToValue={(option, value) => {
                            const optRnc = typeof option === "string" ? option : option?.rnc;
                            const valRnc = typeof value === "string" ? value : value?.rnc;
                            return String(optRnc || "").trim() === String(valRnc || "").trim();
                          }}
                          filterOptions={(x) => x}
                          onInputChange={(_, value, reason) => {
                            if (reason === "input" || reason === "clear") {
                              setRnc(value || "");
                              if (busquedaRncTimerRef.current) {
                                clearTimeout(busquedaRncTimerRef.current);
                              }
                              busquedaRncTimerRef.current = setTimeout(() => {
                                buscarRncs(value || "");
                              }, 280);
                            }
                          }}
                          onChange={(_, option) => {
                            if (option && typeof option === "object") {
                              setRnc((option.rnc || "").trim());
                              if ((option.descripcion || "").trim()) {
                                setRazonSocial(option.descripcion.trim());
                              }
                            } else if (typeof option === "string") {
                              setRnc(option.trim());
                            } else if (option == null) {
                              setRnc("");
                            }
                          }}
                          renderOption={(props, option) => (
                            <li {...props} key={`${option.rnc}-${option.descripcion}`}>
                              <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                                  {option.rnc}
                                </Typography>
                                <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
                                  {option.descripcion || "—"}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required
                              label="RNC / Cédula"
                              helperText={
                                e32RequiereRnc
                                  ? "Requerido: consumo de RD$250,000 o más (DGII)"
                                  : "Busque en el catálogo RNC o escriba uno nuevo"
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={fpInputSx}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {cargandoRnc ? (
                                      <CircularProgress color="inherit" size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={7}>
                        <TextField
                          fullWidth
                          required
                          label="Razón social / Nombre"
                          value={razonSocial}
                          onChange={(e) => setRazonSocial(e.target.value)}
                          helperText={
                            e32RequiereRnc
                              ? "Requerido: consumo de RD$250,000 o más (DGII)"
                              : "Requerido para documentos distintos de Consumo (E32)"
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={fpInputSx}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Garantía / Observaciones"
                      value={garantia}
                      onChange={(e) => setGarantia(e.target.value)}
                      multiline
                      rows={2}
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Items */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1.5, pb: 1.2, borderBottom: `1px solid ${FP.color.border}` }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.2,
                      bgcolor: FP.color.accentBlueSoft,
                      color: FP.color.accentBlue,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ShoppingCartIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
                    Items de la factura
                  </Typography>
                </Stack>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: `1px solid ${FP.color.border}`,
                    borderRadius: 2,
                    overflow: "auto",
                    mb: 2,
                    maxHeight: "360px",
                  }}
                >
                  <Table size="small" sx={itemsTableSx}>
                    {itemsColgroup}
                    <TableBody>
                      <TableRow
                        sx={{
                          "& td": {
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            border: "none",
                            borderBottom: `2px solid ${FP.color.border}`,
                            bgcolor: FP.color.surfaceAlt,
                          },
                        }}
                      >
                        <TableCell sx={itemHeadLabelSx}>Chasis</TableCell>
                        <TableCell sx={itemHeadLabelSx}>Descripción</TableCell>
                        <TableCell align="center" sx={itemHeadLabelSx}>Cant.</TableCell>
                        <TableCell align="right" sx={itemHeadLabelSx}>Precio</TableCell>
                        <TableCell align="center" sx={itemHeadLabelSx}>ITBIS</TableCell>
                        <TableCell align="right" sx={itemHeadLabelSx}>Descuento</TableCell>
                        <TableCell align="right" sx={itemHeadLabelSx}>Subtotal</TableCell>
                        <TableCell align="center" sx={itemHeadLabelSx}>Acc.</TableCell>
                      </TableRow>
                      {itemsFactura.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ color: FP.color.textSecondary, py: 4 }}>
                            No hay items en la factura
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsFactura.map((item) => {
                          const subtotalItem =
                            toNumber(item.cantidad) * toNumber(item.precio) - toNumber(item.descuento);
                          return (
                            <TableRow key={item.id} hover>
                              <TableCell sx={{ overflow: "hidden" }} title={item.chasis}>
                                <Chip
                                  label={item.chasis}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    maxWidth: "100%",
                                    bgcolor: FP.color.accentBlueSoft,
                                    color: FP.color.accentBlue,
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 500,
                                  color: FP.color.textPrimary,
                                  fontSize: "0.82rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={item.descripcion}
                              >
                                <Box
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {item.descripcion}
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.3 }}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "0.72rem",
                                      color: FP.color.textSecondary,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Dueño:
                                  </Typography>
                                  <TextField
                                    variant="standard"
                                    placeholder="Nombre del dueño"
                                    value={item.dueno || ""}
                                    onChange={(e) => actualizarItem(item.id, "dueno", e.target.value)}
                                    inputProps={{
                                      maxLength: 150,
                                      "aria-label": "Dueño del chasis",
                                      style: { fontSize: "0.75rem" },
                                    }}
                                    fullWidth
                                    sx={{ "& .MuiInput-input": { p: 0 } }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.2,
                                    bgcolor: FP.color.surfaceAlt,
                                    borderRadius: 1.4,
                                    p: 0.3,
                                    width: "fit-content",
                                    mx: "auto",
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    //onClick={() => actualizarCantidad(item.id, toNumber(item.cantidad) - 1)}
                                    sx={{ color: FP.color.accentBlue, p: 0.3 }}
                                  >
                                    <RemoveIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                  <Typography
                                    sx={{
                                      minWidth: "26px",
                                      textAlign: "center",
                                      fontWeight: 700,
                                      color: FP.color.textPrimary,
                                      fontSize: "0.85rem",
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    {toNumber(item.cantidad)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    //onClick={() => actualizarCantidad(item.id, toNumber(item.cantidad) + 1)}
                                    sx={{ color: FP.color.accentBlue, p: 0.3 }}
                                  >
                                    <AddIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  variant="standard"
                                  value={item.precio}
                                  onChange={(e) => actualizarItem(item.id, "precio", e.target.value)}
                                  inputProps={{ style: { textAlign: "right", fontSize: "0.82rem" } }}
                                  sx={{ "& .MuiInput-input": { p: 0 } }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  select
                                  variant="standard"
                                  value={esEspecial ? "0" : item.tasaitbis}
                                  onChange={(e) => actualizarItem(item.id, "tasaitbis", e.target.value)}
                                  disabled={true}
                                  SelectProps={{ disableUnderline: false }}
                                  sx={{ "& .MuiSelect-select": { fontSize: "0.8rem", py: 0 } }}
                                >
                                  {TASAS_ITBIS.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                      {t.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  variant="standard"
                                  value={item.descuento}
                                  disabled={true}
                                  onChange={(e) => actualizarItem(item.id, "descuento", e.target.value)}
                                  inputProps={{ style: { textAlign: "right", fontSize: "0.82rem" } }}
                                  sx={{ "& .MuiInput-input": { p: 0 } }}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 800,
                                  color: FP.color.navy,
                                  fontSize: "0.85rem",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                ${formatMoney(subtotalItem)}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  aria-label="Quitar chasis"
                                  onClick={() => eliminarItem(item.id)}
                                  sx={{ color: FP.color.danger }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                      {itemsFactura.length > 0 &&
                        (incluirPlaca ? (
                          <TableRow hover>
                            <TableCell>
                              <Chip
                                label={CODIGO_PLACA}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.7rem",
                                  bgcolor: FP.color.surfaceAlt,
                                  color: FP.color.textSecondary,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.82rem", color: FP.color.textPrimary }}>
                              Placa exenta
                              <Typography
                                component="div"
                                sx={{ fontSize: "0.72rem", color: FP.color.textSecondary }}
                              >
                                {itemsFactura.length} chasis × monto fijo
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 700, fontSize: "0.85rem", fontVariantNumeric: "tabular-nums" }}
                            >
                              {itemsFactura.length}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                variant="standard"
                                value={montoPlaca}
                                placeholder="0.00"
                                onChange={(e) => setMontoPlaca(e.target.value)}
                                inputProps={{
                                  inputMode: "decimal",
                                  "aria-label": "Monto fijo de la placa por chasis",
                                  style: { textAlign: "right", fontSize: "0.82rem" },
                                }}
                                sx={{ "& .MuiInput-input": { p: 0 } }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "0.8rem" }}>
                              Exento
                            </TableCell>
                            <TableCell align="right" sx={{ color: FP.color.textSecondary }}>
                              —
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 800,
                                color: FP.color.navy,
                                fontSize: "0.85rem",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              ${formatMoney(itemsFactura.length * toNumber(montoPlaca))}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                aria-label="Quitar placa"
                                onClick={() => setIncluirPlaca(false)}
                                sx={{ color: FP.color.danger }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ py: 0.5 }}>
                              <Button
                                size="small"
                                startIcon={<AddIcon fontSize="small" />}
                                onClick={agregarPlaca}
                                sx={{ textTransform: "none", fontSize: "0.8rem" }}
                              >
                                Agregar placa (exenta)
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Cargos + Totales */}
              <Box sx={{ flexShrink: 0 }}>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Descuento General"
                      value={descuentoGeneral}
                      onChange={(e) => setDescuentoGeneral(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={fpInputSx}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Envío"
                      value={envio}
                      onChange={(e) => setEnvio(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={fpInputSx}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Otros Cargos"
                      value={otrosCargos}
                      onChange={(e) => setOtrosCargos(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={fpInputSx}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: FP.color.surfaceAlt, border: `1px solid ${FP.color.border}` }}>
                  {[
                    ["Subtotal", totales.subtotal],
                    ["Descuento", totales.descTotal],
                    ["Monto Gravado", totales.gravado],
                    ["Monto Exento", totales.exento],
                    ["ITBIS", totales.itbis],
                    ["Envío", totales.env],
                    ["Otros Cargos", totales.otros],
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.84rem" }}>{label}:</Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: label === "Descuento" && value > 0 ? FP.color.danger : FP.color.textPrimary,
                          fontSize: "0.85rem",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {label === "Descuento" && value > 0 ? "-" : ""}${formatMoney(value)}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5, borderColor: FP.color.border }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1.6,
                      borderRadius: 1.6,
                      bgcolor: FP.color.navy,
                      color: "#FFFFFF",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>Total General</Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "1.4rem",
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      ${formatMoney(totales.totalGeneral)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1.4 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={limpiarFactura}
                    fullWidth
                    sx={{
                      py: 1.2,
                      textTransform: "none",
                      borderRadius: 1.4,
                      fontWeight: 700,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: "#FFFFFF",
                      "&:hover": { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
                    }}
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={manejarGuardarClick}
                    fullWidth
                    disabled={itemsFactura.length === 0 || generando}
                    sx={{
                      py: 1.2,
                      textTransform: "none",
                      borderRadius: 1.4,
                      fontWeight: 700,
                      bgcolor: FP.color.navy,
                      color: "#FFFFFF",
                      boxShadow: "none",
                      "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                      "&.Mui-disabled": { bgcolor: FP.color.border, color: FP.color.textMuted },
                    }}
                  >
                    {generando ? "Generando..." : "Guardar"}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PagoFactura
        open={dialogoPagoAbierto}
        total={totales.totalGeneral}
        formasPago={formasPagoDb}
        onClose={() => setDialogoPagoAbierto(false)}
        onConfirm={(datosPago) => {
          setDialogoPagoAbierto(false);
          generarFactura(datosPago);
        }}
      />

      <VistaFactura
        open={dialogoImpresionAbierto}
        facturaData={datosImpresion || {}}
        onClose={() => {
          setDialogoImpresionAbierto(false);
          resetFormulario();
          cargarTiposComprobante();
        }}
      />
    </Box>
  );
};

export default FacturasElectronicasDgii;
